import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAPI } from './useAPIs';
import { LAT_SIGNALS, LNG_SIGNALS, GPS_SCALE_FACTOR, isValidGpsCoord, getSafeTimestamp } from '../VisualizeData/visualizeData.constants';
import { useNotification } from '../common/NotificationContext';


const toDisplayName = (signalName, network) =>
  network ? `${signalName} (${network})` : signalName;

const encodeSignalId = (signalName, network) =>
  network ? `${signalName}|${network}` : signalName;

const decodeSignalId = (sigId) => {
  const [signalName, network = ''] = sigId.split('|');
  return { signalName, network };
};

const toSignalKey = (signalName, network) => `${signalName}|${network || ''}`;

const mapChartData = (dataPoints) => {
  if (!Array.isArray(dataPoints)) {
    return [];
  }

  return dataPoints.map((p) => ({
    timestamp: p.timestamp,
    value_num:
      typeof p.val_num === 'string'
        ? parseFloat(p.val_num)
        : (p.val_num ?? 0),
    value_str: p.val_str ?? p.val_num,
  }));
};

const buildProcessedSignal = (data, signalName, network, selectionId) => ({
  signalName: toDisplayName(signalName, network),
  originalSignalName: signalName,
  chartData: mapChartData(data?.data),
  network,
  networkMapping: data?.network_mapped_to || null,
  selectionId,
  signalKey: toSignalKey(signalName, network),
});

const processExtractedSignals = (data, signalName, requestedNetwork) => {
  const selectionId = encodeSignalId(signalName, requestedNetwork);
  const hasMergedGroupedSeries = !requestedNetwork && Array.isArray(data?.data) && data.data.length > 0;

  if (hasMergedGroupedSeries) {
    return [buildProcessedSignal(data, signalName, requestedNetwork, selectionId)];
  }

  if (Array.isArray(data?.network_results) && data.network_results.length > 0) {
    return data.network_results
      .map((networkResult) => {
        const resolvedNetwork = networkResult?.network || requestedNetwork || '';
        return buildProcessedSignal(networkResult, signalName, resolvedNetwork, selectionId);
      })
      .filter((signalItem) => signalItem.chartData.length > 0 || signalItem.network);
  }

  return [buildProcessedSignal(data, signalName, requestedNetwork, selectionId)];
};

const mergeSignals = (previousSignals, nextSignals) => {
  if (!Array.isArray(previousSignals) || previousSignals.length === 0) {
    return Array.isArray(nextSignals) ? nextSignals.slice() : [];
  }
  if (!Array.isArray(nextSignals) || nextSignals.length === 0) {
    return previousSignals;
  }

  const signalMap = new Map(
    previousSignals.map((signalItem) => [signalItem.signalKey, signalItem]),
  );

  nextSignals.forEach((signalItem) => {
    const existing = signalMap.get(signalItem.signalKey);
    if (!existing || existing !== signalItem) {
      signalMap.set(signalItem.signalKey, signalItem);
    }
  });
  return Array.from(signalMap.values());
};

export const useBuslogSignals = (filePath) => {
  const { apiCall } = useAPI();
  const [searchParams, setSearchParams] = useSearchParams();
  const notify = useNotification();

  const [visualizedSignals, setVisualizedSignals] = useState([]);
  const [overlayTimeFilter, setOverlayTimeFilter] = useState('all');

  // GPS signals extracted for the map — kept separate from user-selected signals
  const [gpsSignals, setGpsSignals] = useState([]);
  const [isLoadingGps, setIsLoadingGps] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const gpsLoadedForFile = useRef(null);

  // Per-signal loading state: Set of "displayName" strings currently being extracted.
  const [loadingSignals, setLoadingSignals] = useState(new Set());

  const extractSignalMutation = useMutation({
    mutationFn: async ({ signalName, network }) => {
      const response = await apiCall('POST', '/api/v1/signals/extract/', {
        file_path: filePath,
        signal_name: signalName,
        ...(network && { network }),
      });
      if (response.status !== 200) {
        throw new Error(`Extract failed: ${response.statusText}`);
      }
      return response.data;
    },
    onMutate: ({ displayName }) => {
      setLoadingSignals((prev) => new Set(prev).add(displayName));
    },
    onSuccess: (data, { signalName, network, displayName }) => {
      if (data.error) {
        notify({
          message: data.error + (data.hint ? ` ${data.hint}` : ''),
          severity: 'error',
        });
        return;
      }

      const processedSignals = processExtractedSignals(data, signalName, network);
      setVisualizedSignals((prev) => mergeSignals(prev, processedSignals));

      if (data.is_ambiguous && data.supports_grouped_extraction) {
        const networkCount = processedSignals.length;
        notify({
          message: `${displayName} loaded for ${networkCount} network${networkCount === 1 ? '' : 's'}`,
          severity: 'success',
        });
        return;
      }

      notify({ message: `${displayName} loaded`, severity: 'success' });
    },
    onError: (error, { displayName }) => {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to load signal data';
      const hint = error.response?.data?.hint || '';
      notify({
        message: `${displayName}: ${msg}${hint ? ` ${hint}` : ''}`,
        severity: 'error',
      });
    },
    onSettled: (_data, _error, { displayName }) => {
      setLoadingSignals((prev) => {
        const next = new Set(prev);
        next.delete(displayName);
        return next;
      });
    },
  });

  // Load signals from URL or saved preferences on mount
  const initializedSignalsFromUrl = useRef(false);
  useEffect(() => {
    if (initializedSignalsFromUrl.current || !filePath) return;
    initializedSignalsFromUrl.current = true;

    let signalsParam = searchParams.get('signals');

    if (!signalsParam) {
      try {
        const saved = localStorage.getItem('visualize_signals_preferences');
        if (saved) {
          signalsParam = saved;
          setSearchParams(prev => {
            prev.set('signals', saved);
            return prev;
          }, { replace: true });
        }
      } catch (e) {
        console.warn('Could not read saved signal preferences', e);
      }
    }

    if (signalsParam) {
      signalsParam.split(',').forEach(sig => {
        const { signalName, network } = decodeSignalId(sig);
        const displayName = toDisplayName(signalName, network);
        extractSignalMutation.mutate({ signalName, network, displayName });
      });
    }
  }, [filePath, searchParams, extractSignalMutation, setSearchParams]);

  // ---- Extract GPS signals for map (called once when map opens) ----
  const loadGpsSignals = useCallback(async () => {
    if (!filePath || gpsLoadedForFile.current === filePath) return;
    gpsLoadedForFile.current = filePath;
    setIsLoadingGps(true);
    setGpsError(null);

    const gpsNames = [...LAT_SIGNALS, ...LNG_SIGNALS];

    try {
      const settled = await Promise.allSettled(
        gpsNames.map(async (sigName) => {
          const response = await apiCall('POST', '/api/v1/signals/extract/', {
            file_path: filePath,
            signal_name: sigName,
          });
          if (response.status === 200 && !response.data?.error) {
            return buildProcessedSignal(response.data, sigName, '', encodeSignalId(sigName, ''));
          }
          return null;
        }),
      );

      const results = settled
        .filter((r) => r.status === 'fulfilled' && r.value != null)
        .map((r) => r.value);

      setGpsSignals(results);
    } catch (err) {
      setGpsError(err.message || 'Failed to load GPS signals');
    } finally {
      setIsLoadingGps(false);
    }
  }, [filePath, apiCall]);

  // ---- Derive GPS map data from extracted GPS signals ----
  const gpsMapData = useMemo(() => {
    const latSigs = gpsSignals.filter((s) => LAT_SIGNALS.has(s.originalSignalName));
    const lngSigs = gpsSignals.filter((s) => LNG_SIGNALS.has(s.originalSignalName));

    if (latSigs.length === 0 || lngSigs.length === 0) {
      return { gpsFrames: [], routePositions: [], bounds: null, hasGps: false };
    }

    const DEDUP_RESOLUTION = 1;
    const roundTs = (ts) => Math.round(ts / DEDUP_RESOLUTION) * DEDUP_RESOLUTION;

    const latMap = new Map();
    for (const sig of latSigs) {
      for (const pt of sig.chartData) {
        const ts = roundTs(getSafeTimestamp(pt.timestamp));
        if (!latMap.has(ts)) {
          latMap.set(ts, pt.value_num * GPS_SCALE_FACTOR);
        }
      }
    }

    // Build a deduplicated lng lookup: {roundedTs → degrees}
    const lngMap = new Map();
    for (const sig of lngSigs) {
      for (const pt of sig.chartData) {
        const ts = roundTs(getSafeTimestamp(pt.timestamp));
        if (!lngMap.has(ts)) {
          lngMap.set(ts, pt.value_num * GPS_SCALE_FACTOR);
        }
      }
    }

    const frames = [];
    for (const [ts, lng] of lngMap) {
      const lat = latMap.get(ts);
      if (lat == null) continue;
      if (!isValidGpsCoord(lat, lng)) continue;
      frames.push({ time: ts, lat, lng });
    }

    frames.sort((a, b) => a.time - b.time);

    const routePositions = frames.map((f) => [f.lat, f.lng]);

    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;
    for (const f of frames) {
      if (f.lat < minLat) minLat = f.lat;
      if (f.lat > maxLat) maxLat = f.lat;
      if (f.lng < minLng) minLng = f.lng;
      if (f.lng > maxLng) maxLng = f.lng;
    }
    const bounds = [[minLat, minLng], [maxLat, maxLng]];

    return { gpsFrames: frames, routePositions, bounds, hasGps: true };
  }, [gpsSignals]);

  const selectSignal = useCallback(
    (signal) => {
      if (!signal || !filePath) return;

      const signalName = signal.signal;
      const network = signal.network || '';

      if (!signalName) {
        notify({ message: 'No signal name found', severity: 'error' });
        return;
      }

      const displayName = toDisplayName(signalName, network);

      const selectionId = encodeSignalId(signalName, network);
      const existingSignals = visualizedSignals.filter((s) => s.originalSignalName === signalName);
      const hasExactSignal = existingSignals.some((s) => s.network === network);
      const hasSelection = existingSignals.some((s) => s.selectionId === selectionId);
      const hasAnySignalSelection = existingSignals.length > 0;

      const shouldPreventDuplicateSelection = () => {
        // Prevent adding the exact same network signal again
        if (network && hasExactSignal) return true;
        // Prevent adding a grouped signal if it's already selected
        if (!network && hasSelection) return true;
        // Prevent adding a grouped signal if any individual network signals are already selected
        if (!network && hasAnySignalSelection) return true;
        
        return false;
      };

      if (shouldPreventDuplicateSelection()) {
        notify({
          message: 'Signal already visualized. Remove the current selection before switching between grouped and single-network modes.',
          severity: 'warning',
        });
        return;
      }

      extractSignalMutation.mutate({ signalName, network, displayName });

      setSearchParams(prev => {
        const currentSignals = prev.get('signals') ? prev.get('signals').split(',') : [];
        const sigId = encodeSignalId(signalName, network);
        if (!currentSignals.includes(sigId)) {
          currentSignals.push(sigId);
          prev.set('signals', currentSignals.join(','));
        }
        return prev;
      }, { replace: true });
    },
    [filePath, visualizedSignals, extractSignalMutation, setSearchParams]
  );

  const removeSignal = useCallback((identifierToRemove) => {
    const signalToRemove = visualizedSignals.find(
      (signalItem) =>
        signalItem.signalName === identifierToRemove ||
        signalItem.selectionId === identifierToRemove ||
        signalItem.originalSignalName === identifierToRemove
    );
    if (!signalToRemove) {
      return;
    }

    const remainingSignals = visualizedSignals.filter((signalItem) => {
      if (
        signalItem.signalName === identifierToRemove ||
        signalItem.selectionId === identifierToRemove ||
        signalItem.originalSignalName === identifierToRemove
      ) {
        return false;
      }

      if (signalToRemove.selectionId === signalToRemove.originalSignalName) {
        return signalItem.selectionId !== signalToRemove.selectionId;
      }

      return true;
    });

    setVisualizedSignals(remainingSignals);

    setSearchParams(prev => {
      const nextSelectionIds = Array.from(new Set(remainingSignals.map((signalItem) => signalItem.selectionId)));

      if (nextSelectionIds.length > 0) {
        prev.set('signals', nextSelectionIds.join(','));
      } else {
        prev.delete('signals');
      }
      return prev;
    }, { replace: true });
  }, [setSearchParams, visualizedSignals]);

  const clearSignals = useCallback(() => {
    setVisualizedSignals([]);
    setSearchParams(prev => {
      prev.delete('signals');
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const reloadGpsSignals = useCallback(() => {
    gpsLoadedForFile.current = null;
    setGpsSignals([]);
    loadGpsSignals();
  }, [loadGpsSignals]);

  return {
    visualizedSignals,
    overlayTimeFilter,
    isExtracting: extractSignalMutation.isPending,
    loadingSignals: Array.from(loadingSignals),
    selectSignal,
    removeSignal,
    clearSignals,
    setTimeFilter: setOverlayTimeFilter,
    // GPS map data
    gpsMapData,
    isLoadingGps,
    gpsError,
    loadGpsSignals,
    reloadGpsSignals,
  };
};
